import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { errorMessage, useTRPC } from "../../../lib/api";
import { Button, ErrorText, PageHeader, Section, StatusText } from "../../../ui";
import { useToast } from "../../../ui/toast";
import { actionLabel, formatWhen, viaLabel } from "../../approvals/model";
import { SettingsNav } from "../SettingsNav";

const PAGE = 50;

/**
 * Settings → Activity: the audit trail (architecture §9), newest first, with Undo where the
 * action can be reversed and has not been already. Undo runs through the api like any action.
 */
export function ActivityPage() {
  const trpc = useTRPC();
  const queryClient = useQueryClient();
  const toast = useToast();
  const audit = useQuery(trpc.audit.list.queryOptions({ limit: PAGE }));
  const undo = useMutation(
    trpc.audit.undo.mutationOptions({
      onSuccess: async (out) => {
        toast({ message: out.restored === 1 ? "Undone." : `Undone (${out.restored} restored).` });
        await queryClient.invalidateQueries({ queryKey: trpc.audit.list.queryKey() });
      },
      onError: (error) => toast({ tone: "error", message: errorMessage(error) }),
    }),
  );

  return (
    <div className="flex flex-col gap-6">
      <PageHeader title="Activity" />
      <SettingsNav />
      <Section title="Recent changes" description="Everything changed through KitWork+, by you, the command line, agents, rules or AI.">
        {audit.isPending ? <StatusText>Loading…</StatusText> : null}
        {audit.isError ? <ErrorText>{errorMessage(audit.error)}</ErrorText> : null}
        {audit.data !== undefined && audit.data.length === 0 ? <p className="text-sm text-muted">Nothing yet.</p> : null}
        {audit.data !== undefined && audit.data.length > 0 ? (
          <div className="overflow-x-auto">
            <table className="w-full text-left text-sm">
              <caption className="sr-only">Recent changes</caption>
              <thead className="text-xs uppercase tracking-wide text-muted">
                <tr>
                  <th scope="col" className="py-2 pr-3 font-medium">
                    When
                  </th>
                  <th scope="col" className="hidden py-2 pr-3 font-medium sm:table-cell">
                    Who
                  </th>
                  <th scope="col" className="py-2 pr-3 font-medium">
                    What
                  </th>
                  <th scope="col" className="py-2 font-medium">
                    <span className="sr-only">Undo</span>
                  </th>
                </tr>
              </thead>
              <tbody>
                {audit.data.map((entry) => (
                  <tr key={entry.id} className="border-t border-border align-top">
                    <td className="py-2 pr-3 text-muted">{formatWhen(entry.at)}</td>
                    <td className="hidden py-2 pr-3 text-muted sm:table-cell">{viaLabel(entry.actor.via)}</td>
                    <td className="py-2 pr-3">
                      <p className="font-medium text-text">{entry.undoOf === null ? actionLabel(entry.action) : `Undo: ${actionLabel(entry.action)}`}</p>
                      <p className="break-words text-muted">{entry.summary}</p>
                    </td>
                    <td className="py-2 text-right">
                      {entry.undoneBy !== null ? (
                        <span className="text-xs text-muted">Undone</span>
                      ) : entry.undoable ? (
                        <Button
                          disabled={undo.isPending}
                          aria-label={`Undo ${entry.summary}`}
                          onClick={() => undo.mutate({ auditId: entry.id })}
                        >
                          Undo
                        </Button>
                      ) : null}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : null}
      </Section>
    </div>
  );
}
